import {
  ApiErrorResponse,
  BookmarkCreate,
  CustomPagination,
  DetailLayout,
  ErrorAlertWithDrawer,
  FormRef,
  InfoIconUrl,
  InputDataDetailsType,
  InputDataType,
  ISODate,
  Padding,
  SampleCommentType,
  SubmittedDataVersionType,
  TotalEntries,
  useQueryParams,
} from '@atlas-frontend/sdk';
import { RefObject } from 'react';
import { Alert, PaginationProps } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from '../InputDataSubjectDetail.translations.json';
import styled from 'styled-components';
import { InputDataSubjectHeader } from '../InputDataSubjectHeader';
import { InputDataDetailsPanel } from '../InputDataDetailsPanel';
import { TableFilterSortProps } from '../../table/InputDataViewTable';
import { useInputDataDetailsStoreValue } from '../InputDataDetailsContext';
import { InputDataDescriptionInfo, InputDataVersionInfo } from '../types';
import { InputDataSubjectActions } from '../InputDataSubjectActions';
import { InputDataSubjectDetailLeftSideHeader } from '../leftSide/InputDataSubjectDetailLeftSideHeader';
import { InputDataSubjectDetailLeftSideContent } from '../leftSide/InputDataSubjectDetailLeftSideContent';
import { InputDataSubjectDetailLeftSideFooter } from '../leftSide/InputDataSubjectDetailLeftSideFooter';
import { useInputDataComments } from './useInputDataComments';
import { OutputSnapshotVersionType } from '@atlas-frontend/sdk';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-sample-detail',
);
addTranslations(translations);

interface InputDataSubjectDetailViewProps {
  valueDate: ISODate;
  subject: string;
  inputDataDetailsType?: InputDataDetailsType;
  descriptionData: InputDataDescriptionInfo;
  detailsData?: InputDataType;
  detailsError?: ApiErrorResponse | null;
  subjectNameMappings?: Record<string, string>;
  output?: OutputSnapshotVersionType;
  selectedSubmittedDataId?: number;
  onSelectSubmittedDataId?: (submittedDataId: number) => void;
  selectedVersion?: SubmittedDataVersionType;
  versionData: InputDataVersionInfo;
  formRef?: RefObject<FormRef<any> | null>;
  canManage: boolean;
  onReload: () => Promise<any>;
  isLoading: boolean;
  onAddComment: (comment: SampleCommentType) => void;
  paginationProps?: PaginationProps;
  tableProps?: TableFilterSortProps;
}

export function InputDataSubjectDetailView(
  props: InputDataSubjectDetailViewProps,
) {
  const {
    inputDataDetailsType,
    descriptionData,
    detailsData,
    selectedVersion,
    versionData,
    subjectNameMappings,
    selectedSubmittedDataId,
    onSelectSubmittedDataId,
  } = props;
  const [{ outputDataVersionId }] = useQueryParams();
  const { description } = descriptionData;
  const { segment, commentsTotal, inputComment, dataLoadingError } =
    useInputDataComments(
      inputDataDetailsType,
      selectedVersion,
      props.detailsError,
    );

  return (
    <DetailLayout
      infoIconUrl={InfoIconUrl.INPUT_DATA_SUBJECT_DETAIL}
      breadCrumb={
        <InputDataSubjectHeader
          data={inputDataDetailsType}
          descriptionData={descriptionData}
          selectedSubmittedDataId={selectedSubmittedDataId}
          onSelectSubmittedDataId={onSelectSubmittedDataId}
          output={props.output}
          versionData={versionData}
          onReload={props.onReload}
          isLoading={props.isLoading}
        />
      }
      bookmarkCreate={
        <BookmarkCreate
          excludeUrlParams={!outputDataVersionId ? ['date'] : []}
          includeUrlParams={
            !outputDataVersionId
              ? [{ key: 'periodSkipLastValueDate', value: 'true' }]
              : []
          }
        />
      }
      headerConfig={{
        actions: (
          <InputDataSubjectActions
            data={inputDataDetailsType}
            descriptionData={descriptionData}
            columnCount={detailsData?.columns?.length}
            versionData={versionData}
            versionId={selectedVersion?.id}
            canManage={props.canManage}
            onReload={props.onReload}
            isLoading={props.isLoading}
          />
        ),
      }}
      side={{
        header: (
          <InputDataSubjectDetailLeftSideHeader
            description={description}
            inputDataDetailsType={inputDataDetailsType}
          />
        ),
        content: (
          <InputDataSubjectDetailLeftSideContent
            inputDataDetailsType={inputDataDetailsType}
            output={props.output}
            selectedSubmittedDataId={props.selectedSubmittedDataId}
            selectedVersion={selectedVersion}
            versionData={versionData}
            inputComment={inputComment}
          />
        ),
        footer: (
          <InputDataSubjectDetailLeftSideFooter
            description={description}
            inputDataDetailsType={inputDataDetailsType}
            selectedVersion={selectedVersion}
            inputComment={inputComment}
          />
        ),
      }}
      footerContent={
        <FooterWrapper>
          <TotalEntries
            count={
              segment === 'data'
                ? (props.paginationProps?.total ?? 0)
                : commentsTotal
            }
          />
          {segment === 'data' && props.paginationProps && (
            <FooterPagination {...props.paginationProps} />
          )}
        </FooterWrapper>
      }
    >
      <ViewAlerts />
      {dataLoadingError ? (
        <Alert type="error" message={dataLoadingError} />
      ) : (
        <Padding>
          <InputDataDetailsPanel
            data={detailsData}
            comments={inputDataDetailsType?.sampleComments ?? []}
            date={props.valueDate}
            description={description}
            descriptions={inputDataDetailsType?.descriptions ?? []}
            sampleId={inputDataDetailsType?.sampleId ?? undefined}
            loadingStatus={
              versionData.version === 'output'
                ? !props.output
                  ? 'NO_OUTPUT_SELECTED'
                  : undefined
                : inputDataDetailsType?.loadingStatus
            }
            subject={props.subject}
            version={versionData.version}
            groupId={inputDataDetailsType?.dataObjectGroupId ?? undefined}
            formRef={props.formRef}
            isLoading={props.isLoading}
            onAddComment={props.onAddComment}
            tableProps={props.tableProps}
            subjectNameMappings={subjectNameMappings}
          />
        </Padding>
      )}
    </DetailLayout>
  );
}

function ViewAlerts() {
  const [mutationErrors, setMutationErrors] =
    useInputDataDetailsStoreValue('mutationErrors');
  const { t } = useTranslation();

  if (!mutationErrors) {
    return null;
  }

  return (
    <ErrorAlertWithDrawer
      validationMessagesMap={mutationErrors.errors}
      errorAction={t('action.delete')}
      closable={true}
      onClose={() => setMutationErrors(undefined)}
    />
  );
}

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const FooterWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-right: 20px;
  width: 100%;
`;
